/**
 * (2) Que el codigo de convivencia se pueda SELECCIONAR y COPIAR (T10).
 *
 * SINTOMA: en `/registro` (y en `/n8n-chile/solicitar`, que comparte el markup) la linea
 * «Acepto el código de convivencia y los términos y condiciones» no se podia copiar. El texto
 * vive DENTRO del `<label>` de la casilla, y arrastrar el raton sobre un label termina en un
 * `click` cuya accion por defecto es activar el control asociado: se intentaba seleccionar y
 * lo que pasaba era que la casilla cambiaba de estado.
 *
 * No es un problema de `user-select` — medido en vivo, el label ya computaba
 * `user-select: auto` — sino de la accion por defecto del `<label>`.
 *
 * FIX: cancelar esa accion por defecto SOLO cuando el gesto fue de seleccionar, no de pulsar.
 * Un clic normal sigue marcando la casilla, que es lo que espera cualquiera y lo que mantiene
 * el area de toque de 44px del Spec (checklist 8/16).
 *
 * COMO SE DISTINGUE UN GESTO DE OTRO — y por que NO se mira `getSelection()`:
 * inspeccionar la seleccion en el `click` parece lo obvio y da un falso positivo real, medido
 * con Playwright: al pulsar DENTRO de un texto ya seleccionado, Chrome mantiene la seleccion
 * hasta el `mouseup` (esta decidiendo si es el comienzo de un arrastre de texto), asi que el
 * `click` ve seleccion viva y la casilla se quedaba muerta hasta un segundo clic. Se mira el
 * GESTO en su lugar:
 *   - recorrido del raton entre `mousedown` y `click` > 4px  -> es un arrastre de seleccion;
 *   - `detail >= 2` -> doble/triple clic, el gesto de seleccionar palabra o linea.
 * Todo lo demas marca la casilla, incluido un clic sobre texto ya seleccionado (ahi lo util
 * es marcar: la copia ya se hizo).
 *
 * `tagName === 'INPUT'` sale antes de todo: al pulsar el texto el navegador reenvia la
 * activacion a la casilla, que emite su PROPIO click y vuelve a burbujear hasta aqui. Esa
 * segunda pasada no debe bloquearse nunca.
 */
const DRAG_THRESHOLD_PX = 4;

/**
 * Donde empezo la pulsacion en curso. Uno solo para todos los labels: no se puede tener dos
 * botones del raton apretados en dos sitios a la vez. Se CONSUME en el `click` para que un
 * gesto abortado (arrastrar fuera y soltar, que no produce `click`) no pueda contaminar un
 * clic posterior.
 */
let press: { x: number; y: number } | null = null;

export function rememberCheckLabelPress(e: MouseEvent) {
  press = { x: e.clientX, y: e.clientY };
}

export function keepSelectionOnCheckLabel(e: MouseEvent) {
  const from = press;

  press = null;

  const target = e.target as HTMLElement | null;

  // Clic directo sobre la casilla: siempre es intencion de marcar, nunca de seleccionar.
  if (target?.tagName === 'INPUT') return;

  // `detail === 0` es un `click()` sintetico (sin raton): no hay gesto que interpretar.
  if (e.detail < 1) return;

  const dragged = !!from && Math.hypot(e.clientX - from.x, e.clientY - from.y) > DRAG_THRESHOLD_PX;

  if (!dragged && e.detail < 2) return;

  // El gesto fue de seleccionar: se respeta la seleccion y no se toca la casilla.
  e.preventDefault();
}

/**
 * Deshace el toggle que el PRIMER clic de un doble clic ya dejo hecho.
 *
 * El otro gesto para copiar es el doble clic, que elige una palabra. Ahi el guarda de arriba
 * llega tarde por construccion: el primer clic de la secuencia trae `detail === 1` y en ese
 * instante es INDISTINGUIBLE de un clic normal — el navegador todavia no sabe que vendra un
 * segundo —, asi que pasa, el label activa la casilla y solo el segundo clic se bloquea. Neto:
 * la casilla quedaba marcada por intentar copiar una palabra. Reproducido con Playwright
 * (`t10.spec.ts`, «doble clic ...»), no deducido.
 *
 * `dblclick` llega DESPUES de los dos clics, que es el primer momento en que se sabe que el
 * gesto era de seleccion. Como los toggles netos de la secuencia son exactamente uno (el
 * segundo clic ya iba bloqueado), basta con uno inverso para volver al punto de partida — y
 * sirve en los dos sentidos, marcando y desmarcando.
 *
 * Se usa `input.click()` y no `input.checked = ...` a proposito: `click()` dispara tambien el
 * evento `change`, que es de donde el componente se entera (`this.values[name]`). Asignar la
 * propiedad dejaria la casilla y el estado del formulario diciendo cosas distintas.
 */
export function undoCheckLabelDoubleClick(e: MouseEvent) {
  const target = e.target as HTMLElement | null;

  // Doble clic sobre la propia casilla: ahi los dos clics pasaron y se anularon entre si, no
  // hay nada que deshacer — y deshacerlo seria justo introducir el cambio que no hubo.
  if (target?.tagName === 'INPUT') return;

  /*
   * Doble clic sobre uno de los enlaces del texto (PKG-FORO-SMOKE-FIX: «código de convivencia»
   * y «términos y condiciones» ya son `<a>`).
   *
   * Mismo razonamiento que la casilla, por el motivo contrario: un `<a href>` es interactive
   * content, y la especificacion excluye la activacion del `<label>` para los eventos dirigidos
   * a el. O sea que el PRIMER clic de la secuencia tampoco marco nada — no hay toggle neto que
   * deshacer, y el `input.click()` de abajo lo INTRODUCIRIA: doble clic en el enlace y la
   * casilla cambiando sola.
   */
  if (target?.closest('a')) return;

  const label = e.currentTarget as HTMLElement | null;
  const input = label?.querySelector<HTMLInputElement>('input[type="checkbox"]');

  if (!input || input.disabled) return;

  input.click();
}
